import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/apiFetch';
import { getCatalogStorageSession, isCatalogStorageSessionCurrent } from '../utils/catalogSessionStorage';
import { AUTH_SESSION_CHANGING_EVENT, AUTH_SESSION_INVALID_EVENT } from '../utils/auth';

const STATUSES = ['ready', 'sync_pending', 'needs_confirmation', 'incomplete', 'retry_pending', 'checking'];

// Status reads never replace address or checkout drafts. Only the server qualifies an origin.
export default function useOriginSetup(apiUrl) {
  const owner = useRef(getCatalogStorageSession());
  const mounted = useRef(false);
  const generation = useRef(0);
  const controller = useRef(null);
  const [setup, setSetup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const canManage = ['administrador', 'programador'].includes(String(owner.current?.user?.role || '').toLowerCase());
  const request = useCallback(async (reconcile = false) => {
    if (!mounted.current || !isCatalogStorageSessionCurrent(owner.current) || (reconcile && !canManage)) return null;
    const id = ++generation.current;
    controller.current?.abort();
    controller.current = new AbortController();
    const current = () => mounted.current && id === generation.current && isCatalogStorageSessionCurrent(owner.current);
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/origin-setup${reconcile ? '/reconcile' : ''}`, {
        ...(reconcile ? { method: 'POST' } : {}), signal: controller.current.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!current()) return null;
      if (!response.ok || !STATUSES.includes(data.status)) throw new Error(data.error || 'Não foi possível verificar a preparação da localização da loja.');
      const next = data.status === 'ready' && (data.originVerified !== true || data.locationPolicyEnabled !== true)
        ? { ...data, status: 'sync_pending' } : data;
      setSetup(next);
      return next;
    } catch (failure) {
      if (current() && failure.name !== 'AbortError') { setSetup(null); setError(failure.message); }
      return null;
    } finally { if (current()) setBusy(false); }
  }, [apiUrl, canManage]);
  const refresh = useCallback(() => request(false), [request]);
  const reconcile = useCallback(() => request(true), [request]);
  useEffect(() => {
    mounted.current = true;
    const invalidate = () => {
      generation.current += 1;
      controller.current?.abort();
      setSetup(null); setBusy(false); setError('');
    };
    const refreshVisible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const storage = () => { if (!isCatalogStorageSessionCurrent(owner.current)) invalidate(); };
    refresh();
    window.addEventListener('focus', refreshVisible);
    window.addEventListener('storage', storage);
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
    window.addEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
    return () => {
      mounted.current = false; generation.current += 1; controller.current?.abort();
      window.removeEventListener('focus', refreshVisible);
      window.removeEventListener('storage', storage);
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
    };
  }, [refresh]);
  useEffect(() => {
    if (busy) return undefined;
    const timer = window.setTimeout(() => {
      if (document.visibilityState !== 'hidden') refresh();
    }, setup?.status === 'ready' ? 60000 : 15000);
    return () => window.clearTimeout(timer);
  }, [busy, refresh, setup, error]);
  return { setup, busy, error, canManage, refresh, reconcile };
}
